import { decode } from 'jsonwebtoken';
import { camelCase, omit } from 'lodash';

import { getCurrentUserMode } from 'components/Common/LayoutV2/utils';
import Api from 'components/Dashboard/Api';
import { JWT, REFRESH_TOKEN } from 'constants/system';
import { TEST_TYPES, GPA_TYPES } from 'constants/testScores';
import { getDistrictSettings } from 'core/api/slSchools';
import { getUserDetails } from 'core/api/slUsers';
import client from 'core/axios';
import { getCookie, updateCookie } from 'utils/cookies';
import { ensureObject } from 'utils/objects';

import restoreCachedVerticalFilters from 'utils/restoreCachedVerticalFilters';

import { updateProfilePic } from 'modules/AccountSettings/actions';

export const populateOrganizationContext = data => ({
  type: 'POPULATE_ORGANIZATION_CONTEXT',
  data,
});

export const setAlumniInfoLoading = () => ({
  type: 'SET_ALUMNI_INFO_LOADING',
});

export const populateAlumniInfo = data => ({
  type: 'POPULATE_ALUMNI_INFO',
  data,
});

export const retrieveOrganizationContext = () => async (dispatch, getState) => {
  const {
    user: { isMember },
  } = getState();
  if (isMember) {
    try {
      const res = await client.get(`v1/organizations/organization/context/`);
      if (res && res.data) {
        dispatch(populateOrganizationContext(res.data));
      }
      return res.data;
    } catch (e) {
      console.warn('Failed to retrieve organization onboarding', e);
    }
  }
  return null;
};

export const setUserMode = (userMode, localSet = true) => {
  if (localSet) localStorage.setItem('userMode', userMode);
  return {
    type: 'UPDATE_USER_MODE',
    userMode,
  };
};

export const populateUsertypes = userTypesData => ({
  type: 'UPDATE_USER_TYPES',
  userTypesData,
});

export const refreshUsertypes = () => async dispatch => {
  try {
    const { status, data } = await client.get(`v1/sl_users/user/`);
    if (status === 200 && data?.userType) {
      dispatch(
        populateUsertypes({
          userType: data?.userType,
          activeUserType: data?.activeUserType,
          isAlumni: data?.isAlum,
        }),
      );
      const { activeUserType = [] } = ensureObject(data);
      if (
        activeUserType?.includes('guardian') &&
        activeUserType?.includes('member') &&
        !activeUserType?.includes('k12_admin')
      ) {
        dispatch(retrieveOrganizationContext());
      }
    }
  } catch (e) {
    console.warn('could not retrieve user', e);
  }
};

export const populateUserContext = ({
  moduleAccess,
  userCapabilities,
  layout,
  userModes,
  currentUserMode,
}) => ({
  type: 'POPULATE_USER_CONTEXT',
  moduleAccess,
  userCapabilities,
  layout,
  userModes,
  currentUserMode,
});

export const resetUserContext = () => ({
  type: 'RESET_USER_CONTEXT',
});

export const populateUserDetails = ({ payload }) => ({
  type: 'POPULATE_USER_DETAILS',
  payload,
});

export const retrieveUserContext = () => async (dispatch, getState) => {
  const {
    user: { type },
  } = getState();
  try {
    const {
      data: { moduleAccess, userCapabilities, layout, userModes },
    } = await client.get(`v2/sl_users/user-context/`);
    if (
      (type?.includes('guardian') && !type?.includes('member')) ||
      (!type?.includes('guardian') && type?.includes('member'))
    )
      await dispatch(refreshUsertypes());

    const parsedUserCapabilities =
      (Array.isArray(userCapabilities) &&
        userCapabilities.reduce(
          (result, item) => ({
            ...result,
            [camelCase(item.name)]: true,
          }),
          {},
        )) ||
      {};

    const currentUserMode = getCurrentUserMode(userModes)?.mode;

    dispatch(
      populateUserContext({
        moduleAccess: { ...moduleAccess, ...parsedUserCapabilities },
        userCapabilities: userCapabilities || [],
        layout,
        userModes,
        currentUserMode,
      }),
    );
  } catch (e) {
    console.warn('could not retrieve user context', e);
  }
};

export const retrieveAlumniContactInfo = () => async (dispatch, getState) => {
  const {
    user: { affiliations },
  } = getState();

  const district = affiliations.find(
    affiliation =>
      affiliation.organization.organizationType.toLowerCase() === 'district',
  );

  if (district && district.alumniInformation) {
    dispatch(setAlumniInfoLoading());
    try {
      const { data } = await Api.getAlumniContactInfo(
        district.alumniInformation.id,
      );
      dispatch(populateAlumniInfo(data));
    } catch (e) {
      console.warn('could not retrieve alumni contact info', e);
    }
  }
};

export const populateUserModuleAccess = ({ moduleAccess }) => ({
  type: 'POPULATE_USER_MODULE_ACCESS',
  moduleAccess,
});

export const retrieveModuleAccess = studentId => async dispatch => {
  try {
    const url = studentId
      ? `v1/sl_users/module-access/${studentId}/`
      : `v1/sl_users/module-access/`;
    const { status, data } = await client.get(url);
    if (status === 200) {
      dispatch(
        populateUserModuleAccess({
          moduleAccess: data,
        }),
      );
    }
  } catch (e) {
    console.warn('could not retrieve user module access', e);
  }
};

export const populateUserLoginRedirect = data => ({
  type: 'UPDATE_LOGIN_REDIRECT',
  data,
});

export const decodeToken = () => ({
  type: 'DECODE_TOKEN',
});

export const updateExpTime = token => ({
  type: 'UPDATE_EXP_TIME',
  token,
});

export const updateJWT = jwt => {
  const decodedJWT = decode(jwt);
  updateCookie({
    key: JWT,
    value: jwt,
    expires: new Date(decodedJWT.exp * 1000),
  });
};

/*
  Refreshing jwt, refreshes page on local environment but on actual environments it won't refresh the page.
 */
export const tryRefreshingToken = () => async dispatch => {
  try {
    const refreshToken = getCookie(REFRESH_TOKEN);
    const { data, status } = await client.post(
      `v1/sl_users/refresh/`,
      {},
      {
        headers: {
          Authorization: `bearer ${refreshToken}`,
        },
        withCredentials: true,
      },
    );
    if (status === 200) {
      const decodedJWT = decode(data.jwt);
      updateCookie({
        key: JWT,
        value: data.jwt,
        expires: new Date(decodedJWT.exp * 1000),
      });
      dispatch(updateExpTime(data.jwt));
    }
    return { success: status === 200 };
  } catch (e) {
    console.warn('failed to refresh token', e);
  }
  return { success: false };
};

export const populateFeatureOnboarding = data => ({
  type: 'POPULATE_FEATURE_ONBOARDING',
  data,
});

export const retrieveFeatureOnboarding = () => async dispatch => {
  try {
    const res = await client.get(`v1/common/feature-onboarding/`);
    if (res?.data) dispatch(populateFeatureOnboarding(res.data));
  } catch (e) {
    console.warn('Failed to retrieve feature onboarding', e);
  }
  return null;
};

export const completeFeatureOnboarding =
  ({ pk }) =>
  async dispatch => {
    try {
      const { status } = await client.post(`v1/common/feature-onboarding/`, {
        pk,
      });
      if (status === 201) await dispatch(retrieveFeatureOnboarding());
    } catch (e) {
      console.warn('could not complete feature onboarding', e);
    }
    return null;
  };

export const setFeatureOnboardingModalOpen =
  featureOnboardingModalOpen => async dispatch => {
    dispatch({
      type: 'SET_FEATURE_ONBOARDING_MODAL_OPEN',
      featureOnboardingModalOpen,
    });
  };

export const setLoginFailureText = text => ({
  type: 'SET_LOGIN_FAILURE_TEXT',
  text,
});

export const setModuleAccessLoaded = () => ({
  type: 'SET_MODULE_ACCESS_LOADED',
});

export const populateUserBooleans = data => ({
  type: 'POPULATE_USER_BOOLEANS',
  data,
});

export const populateMTPSchool = data => ({
  type: 'POPULATE_MTP_SCHOOL',
  data,
});

export const retrieveUserBooleans = () => async dispatch => {
  try {
    const res = await client.get(`v1/sl_users/user-booleans/`);
    if (res && res.data) {
      dispatch(populateUserBooleans(res.data.results));
    }
    return res;
  } catch (e) {
    console.warn('Failed to retrieve user booleans', e);
  }
  return null;
};

export const completeUserBoolean = type => async () => {
  try {
    await client.post(`v1/sl_users/user-booleans/`, {
      type,
    });
  } catch (e) {
    console.warn('could not update onboarding status', e);
  }
  return null;
};

export const retrieveMTPSchool = () => async dispatch => {
  const path = `api/v1/mtp/schools/`;
  try {
    const { status, data } = await client.get(path);
    if (status === 200) {
      dispatch(populateMTPSchool(data));
    }
  } catch (e) {
    console.warn('Failed to retrieve MTP school', e);
  }
  return null;
};

export const retrieveDefaultTestScores =
  (setDefaultTestType, setDefaultGpaType) => async dispatch => {
    try {
      const { data } = await getDistrictSettings();
      const { defaultTestType, defaultGpaType } = data;

      const defaultTestTypeName = TEST_TYPES[defaultTestType];
      const defaultGpaTypeName = GPA_TYPES[defaultGpaType];

      dispatch(setDefaultTestType(defaultTestTypeName));
      dispatch(setDefaultGpaType(defaultGpaTypeName));
    } catch (e) {
      console.warn('Could not get district settings', e);
    }

    return null;
  };

export const updatePreferredName = preferredName => ({
  type: 'UPDATE_PREFERRED_NAME',
  preferredName,
});

export const retrieveUserDetails = () => async dispatch => {
  try {
    const { data } = await getUserDetails();
    const {
      email,
      firstName,
      isDemoStudent,
      isAlum,
      lastName,
      middleName,
      gradeYear,
      school,
      sisId,
      studentId,
      studentNumber,
      picture,
      phone,
      pk,
      preferredName,
    } = ensureObject(data);
    dispatch(
      populateUserDetails({
        payload: {
          email,
          firstName,
          isDemoStudent,
          isAlumni: isAlum,
          lastName,
          middleName,
          userId: pk,
          profilePic: picture,
          currentStudentGrade: gradeYear,
          sisId,
          studentId,
          studentNumber,
          schoolId: school?.pk,
          schoolName: school?.name,
          district: school?.district,
          preferredName,
          phone,
        },
      }),
    );
    dispatch(updateProfilePic(picture));
  } catch (e) {
    console.warn('Could not populate user details', e);
  }
  return null;
};

export const retrieveDistrictDetails = () => async (dispatch, getState) => {
  const {
    user: { isMember, isGuardian, isAlumni },
  } = getState();

  if (isMember && !(isGuardian || isAlumni)) {
    return;
  }

  try {
    const { data } = await getDistrictSettings();
    const { districtData } = ensureObject(data);

    const {
      id,
      uuid,
      hashId,
      slug,
      timezone,
      name,
      logo,
      website,
      isDemoDistrict,
    } = ensureObject(districtData);

    restoreCachedVerticalFilters(id);

    dispatch(
      populateUserDetails({
        payload: {
          districtName: name,
          districtId: id,
          districtHashId: hashId,
          districtUuid: uuid,
          districtLogo: logo,
          districtAbbrev: slug,
          districtTimezone: timezone,
          districtWebsite: website,
          isDemoDistrict,
          districtSettings: omit(data, 'districtData'),
        },
      }),
    );
  } catch (e) {
    console.warn('Could not populate district details', e);
  }
};
